/* eslint-disable no-console */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-nested-ternary */
/* eslint-disable camelcase */
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import * as Sentry from '@sentry/react';
import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
  useRef,
} from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Chip, Typography } from '@mui/material';
import PayPalLogo from '../../images/PayPal_Logo_Horizontal_Full_Color_RGB.png';
import {
  Luci_Shopping_Api_Models_BillingAddress,
  Luci_Shopping_Api_Models_ShippingAddress,
} from '../../types/api/v7.1';
import { OrderStatus } from '../../types/Order';
import isNonProduction from '../../utilities/isNonProduction';
import { finalizeOrder } from '../../api/ShoppingApi';
import CustomerInfo from '../../types/CustomerInfo';
import Invoice from '../../types/Invoice';
import PaymentFormInfo from '../../types/PaymentFormInfo';
import GAEvent from '../../utilities/analytics';
import { SupportedStatesTerritoriesArmedForces } from '../../utilities/States';
import CreditCardLogos from '../../components/CreditCardLogos';
import {
  emojiCheck,
  phoneNumberRegex,
  usaZipRegex,
} from '../../utilities/validation';
import Styles from './InvoicePortalBilling.module.scss';

interface InvoicePortalBillingProps {
  invoice: Invoice;
  customerInfo: CustomerInfo;
  handleChange: (customerInfo: CustomerInfo) => void;
  invoiceId: string;
  setPaymentFormInfo: (paymentFormInfo: PaymentFormInfo) => void;
  setInvoiceDetails: (invoice: Invoice) => void;
  termsOfUseUrl: string;
  privacyPolicyUrl: string;
  setPaymentType: Dispatch<SetStateAction<string>>;
  paymentType: string;
}

function InvoicePortalBilling({
  invoice,
  customerInfo,
  handleChange,
  invoiceId,
  setPaymentFormInfo,
  setInvoiceDetails,
  termsOfUseUrl,
  privacyPolicyUrl,
  setPaymentType,
  paymentType,
}: InvoicePortalBillingProps): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);

  const errorsArray = searchParams.get('error')?.split(',');

  // Define field paths for easier refactoring and type safety
  const fieldPath = {
    phone: 'phone' as const,
    firstName: 'firstName' as const,
    lastName: 'lastName' as const,
    shippingFirstName: 'shippingAddress.firstName' as const,
    shippingLastName: 'shippingAddress.lastName' as const,
    shippingLine1: 'shippingAddress.line1' as const,
    shippingLine2: 'shippingAddress.line2' as const,
    shippingCity: 'shippingAddress.city' as const,
    shippingState: 'shippingAddress.state' as const,
    shippingZip: 'shippingAddress.zip' as const,
    shippingCountry: 'shippingAddress.country' as const,
    billingFirstName: 'billingAddress.firstName' as const,
    billingLastName: 'billingAddress.lastName' as const,
    billingLine1: 'billingAddress.line1' as const,
    billingLine2: 'billingAddress.line2' as const,
    billingCity: 'billingAddress.city' as const,
    billingState: 'billingAddress.state' as const,
    billingZip: 'billingAddress.zip' as const,
    billingCountry: 'billingAddress.country' as const,
  };

  const compareBillingShipping =
    customerInfo?.shippingAddress?.line1 !==
      customerInfo?.billingAddress?.line1 ||
    customerInfo?.shippingAddress?.line2 !==
      customerInfo?.billingAddress?.line2 ||
    customerInfo?.shippingAddress?.city !==
      customerInfo?.billingAddress?.city ||
    customerInfo?.shippingAddress?.state !==
      customerInfo?.billingAddress?.state ||
    customerInfo?.shippingAddress?.zip !== customerInfo.billingAddress?.zip;
  const { fulfillmentMethod, orderTotal } =
    invoice?.invoiceDetails?.order || {};
  const toBeShipped = fulfillmentMethod === 'Shipping';

  const [loading, setLoading] = useState<boolean>(false);
  const [finalizeOrderError, setFinalizeOrderError] = useState<boolean>(false);
  const [billingSameAsShipping, setBillingSameAsShipping] = useState<boolean>(
    toBeShipped && !errorsArray?.length && !compareBillingShipping
  );

  let orderPaymentCheck = 'Proceed to Pay';
  const hasOutstandingBalance = !!orderTotal && orderTotal > 0;

  if (!hasOutstandingBalance) {
    orderPaymentCheck = 'Complete Order';
  }

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
    watch,
    // eslint-disable-next-line camelcase
  } = useForm<CustomerInfo>({
    mode: 'onChange',
    defaultValues: {
      phone: '',
      firstName: '',
      lastName: '',
      shippingAddress: {
        firstName: '',
        lastName: '',
        line1: '',
        line2: '',
        city: '',
        state: '',
        zip: '',
        country: 'USA',
      },
      billingAddress: {
        firstName: '',
        lastName: '',
        line1: '',
        line2: '',
        city: '',
        state: '',
        zip: '',
        country: 'USA',
      },
    },
    shouldFocusError: false,
  });

  useEffect(() => {
    // First try to use customerInfo (user entered data)
    if (customerInfo) {
      // Set phone
      if (customerInfo.phone) {
        setValue(fieldPath.phone, customerInfo.phone);
      }

      // Set shipping address fields from customerInfo if available
      if (toBeShipped && customerInfo.shippingAddress) {
        setValue(
          fieldPath.shippingFirstName,
          customerInfo.shippingAddress.firstName || ''
        );
        setValue(
          fieldPath.shippingLastName,
          customerInfo.shippingAddress.lastName || ''
        );
        setValue(
          fieldPath.shippingLine1,
          customerInfo.shippingAddress.line1 || ''
        );
        setValue(
          fieldPath.shippingLine2,
          customerInfo.shippingAddress.line2 || ''
        );
        setValue(
          fieldPath.shippingCity,
          customerInfo.shippingAddress.city || ''
        );
        setValue(
          fieldPath.shippingState,
          customerInfo.shippingAddress.state || ''
        );
        setValue(fieldPath.shippingZip, customerInfo.shippingAddress.zip || '');
        setValue(
          fieldPath.shippingCountry,
          customerInfo.shippingAddress.country || 'USA'
        );
      }

      // Set billing address fields from customerInfo if available
      if (customerInfo.billingAddress) {
        setValue(
          fieldPath.billingFirstName,
          customerInfo.billingAddress.firstName || ''
        );
        setValue(
          fieldPath.billingLastName,
          customerInfo.billingAddress.lastName || ''
        );
        setValue(
          fieldPath.billingLine1,
          customerInfo.billingAddress.line1 || ''
        );
        setValue(
          fieldPath.billingLine2,
          customerInfo.billingAddress.line2 || ''
        );
        setValue(fieldPath.billingCity, customerInfo.billingAddress.city || '');
        setValue(
          fieldPath.billingState,
          customerInfo.billingAddress.state || ''
        );
        setValue(fieldPath.billingZip, customerInfo.billingAddress.zip || '');
        setValue(
          fieldPath.billingCountry,
          customerInfo.billingAddress.country || 'USA'
        );
      }
    }
    // Only fall back to invoice data if customerInfo doesn't have necessary fields
    else if (invoice?.invoiceDetails?.order) {
      const { customer, shipToAddress } = invoice.invoiceDetails.order;

      // Initialize phone from customer data
      if (customer?.phone) {
        setValue(fieldPath.phone, customer.phone);
      }

      // Handle shipping address initialization
      if (toBeShipped) {
        let shippingFirstName = '';
        let shippingLastName = '';

        // Determine first and last name from shipToAddress or customer
        if (shipToAddress?.fullName) {
          const nameParts = shipToAddress.fullName.split(' ');
          shippingFirstName = nameParts[0] || '';
          shippingLastName = nameParts.slice(1).join(' ') || '';
        } else if (customer) {
          shippingFirstName = customer.firstName || '';
          shippingLastName = customer.lastName || '';
        }

        // Set shipping address fields
        setValue(fieldPath.shippingFirstName, shippingFirstName);
        setValue(fieldPath.shippingLastName, shippingLastName);

        // Use shipToAddress fields if available, otherwise use customer address
        if (shipToAddress) {
          setValue(fieldPath.shippingLine1, shipToAddress.line1 || '');
          setValue(fieldPath.shippingLine2, shipToAddress.line2 || '');
          setValue(fieldPath.shippingCity, shipToAddress.city || '');
          setValue(fieldPath.shippingState, shipToAddress.state || '');
          setValue(fieldPath.shippingZip, shipToAddress.zip || '');
          setValue(fieldPath.shippingCountry, shipToAddress.country || 'USA');
        } else if (customer?.address) {
          setValue(fieldPath.shippingLine1, customer.address.line1 || '');
          setValue(fieldPath.shippingLine2, customer.address.line2 || '');
          setValue(fieldPath.shippingCity, customer.address.city || '');
          setValue(fieldPath.shippingState, customer.address.state || '');
          setValue(fieldPath.shippingZip, customer.address.zip || '');
          setValue(
            fieldPath.shippingCountry,
            customer.address.country || 'USA'
          );
        }

        // Initialize billing address fields
        setValue(fieldPath.billingFirstName, customer?.firstName || '');
        setValue(fieldPath.billingLastName, customer?.lastName || '');
        if (customer?.address) {
          setValue(fieldPath.billingLine1, customer.address.line1 || '');
          setValue(fieldPath.billingLine2, customer.address.line2 || '');
          setValue(fieldPath.billingCity, customer.address.city || '');
          setValue(fieldPath.billingState, customer.address.state || '');
          setValue(fieldPath.billingZip, customer.address.zip || '');
        }
        setValue(fieldPath.billingCountry, 'USA');
      }
    }
  }, [customerInfo, invoice, setValue, toBeShipped]);

  useEffect(() => {
    if (errorsArray) {
      errorsArray.forEach((entry: string) => {
        switch (entry) {
          case 'address':
            setError('billingAddress.line1', {
              message: 'Could not verify street number',
            });
            break;
          case 'city':
            setError('billingAddress.city', {
              message: 'Could not verify city',
            });
            break;
          case 'state':
            setError('billingAddress.state', {
              message: 'Could not verify state',
            });
            break;
          case 'zipcode':
            setError('billingAddress.zip', {
              message: 'ZIP Code does not match the credit card',
            });
            break;
          default:
        }
      });
    }
  }, []);

  // Clear errors when user starts typing in any field
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name) {
        clearErrors(name);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, clearErrors]);

  const watchedShippingFirstName = watch(fieldPath.shippingFirstName);
  const watchedShippingLastName = watch(fieldPath.shippingLastName);
  const watchedShippingLine1 = watch(fieldPath.shippingLine1);
  const watchedShippingLine2 = watch(fieldPath.shippingLine2);
  const watchedShippingCity = watch(fieldPath.shippingCity);
  const watchedShippingState = watch(fieldPath.shippingState);
  const watchedShippingZip = watch(fieldPath.shippingZip);

  useEffect(() => {
    if (billingSameAsShipping) {
      // Use watched values to ensure we copy the latest from the form's shipping fields
      setValue(fieldPath.billingFirstName, watchedShippingFirstName);
      setValue(fieldPath.billingLastName, watchedShippingLastName);
      setValue(fieldPath.billingLine1, watchedShippingLine1);
      setValue(fieldPath.billingLine2, watchedShippingLine2);
      setValue(fieldPath.billingCity, watchedShippingCity);
      setValue(fieldPath.billingState, watchedShippingState);
      setValue(fieldPath.billingZip, watchedShippingZip);
      setValue(fieldPath.billingCountry, 'USA');
    }
  }, [
    billingSameAsShipping,
    watchedShippingFirstName,
    watchedShippingLastName,
    watchedShippingLine1,
    watchedShippingLine2,
    watchedShippingCity,
    watchedShippingState,
    watchedShippingZip,
  ]);

  const onSubmit = (data: CustomerInfo): void => {
    console.log('[onSubmit] Form data submitted:', data);
    // Format phone number before submitting
    const formattedPhone = data?.phone?.replace(/\D/g, '');

    console.log('[onSubmit] Formatted Phone:', formattedPhone);

    setLoading(true);
    setFinalizeOrderError(false);

    // --- Step 1: Determine the source for billing/shipping details ---
    const useShippingForBilling =
      paymentType === 'PayPal' ||
      (billingSameAsShipping && data.shippingAddress);
    const sourceShippingAddress =
      data.shippingAddress || customerInfo.shippingAddress;
    const sourceBillingAddress = data.billingAddress;

    // --- Step 2: Construct derived addresses for internal state & potential API use ---
    let derivedBillingAddress: Luci_Shopping_Api_Models_BillingAddress | null =
      null;
    if (useShippingForBilling && sourceShippingAddress) {
      derivedBillingAddress = {
        firstName: sourceShippingAddress.firstName || '',
        lastName: sourceShippingAddress.lastName || '',
        line1: sourceShippingAddress.line1 || '',
        line2: sourceShippingAddress.line2 || '',
        city: sourceShippingAddress.city || '',
        state: sourceShippingAddress.state || '',
        zip: sourceShippingAddress.zip || '',
        country: sourceShippingAddress.country || 'USA',
      };
    } else if (sourceBillingAddress) {
      derivedBillingAddress = {
        firstName: sourceBillingAddress.firstName || '',
        lastName: sourceBillingAddress.lastName || '',
        line1: sourceBillingAddress.line1 || '',
        line2: sourceBillingAddress.line2 || '',
        city: sourceBillingAddress.city || '',
        state: sourceBillingAddress.state || '',
        zip: sourceBillingAddress.zip || '',
        country: sourceBillingAddress.country || 'USA',
      };
    } // else remains null

    // Add logging before the PayPal check
    if (isNonProduction) {
      console.log(
        '[onSubmit] BEFORE PayPal check - customerInfo.shippingAddress:',
        customerInfo.shippingAddress
      );
      console.log(
        '[onSubmit] BEFORE PayPal check - data.shippingAddress:',
        data.shippingAddress
      );
    }

    // --- Step 3: Update internal component state (using derived addresses) ---
    // Construct updatedCustomerInfo based directly on form data and flags
    const updatedCustomerInfo: CustomerInfo = {
      ...data, // Start with form data
      billingAddress:
        (billingSameAsShipping || paymentType === 'PayPal') && // Logic based on billingSameAsShipping/PayPal
        data.shippingAddress
          ? { ...data.shippingAddress } // Use shipping if true
          : data.billingAddress, // Otherwise use billing from form
      shippingAddress: toBeShipped ? data.shippingAddress : null, // Use shipping from form if needed
    };
    // Update parent component state if needed (consider if this call is necessary here)
    handleChange(updatedCustomerInfo);

    // --- Step 4: Determine FINAL addresses for the API call ---
    let apiShippingAddress: Luci_Shopping_Api_Models_ShippingAddress | null =
      toBeShipped ? updatedCustomerInfo.shippingAddress : null;
    let apiBillingAddress: Luci_Shopping_Api_Models_BillingAddress | null =
      updatedCustomerInfo.billingAddress;

    if (paymentType === 'PayPal') {
      // **PayPal Specific API Payload Construction**
      // Use shipping details to populate the *API's* billing address field.
      const payPalSourceAddress =
        data.shippingAddress || customerInfo.shippingAddress;
      if (payPalSourceAddress) {
        apiBillingAddress = {
          firstName: payPalSourceAddress.firstName || '',
          lastName: payPalSourceAddress.lastName || '',
          line1: payPalSourceAddress.line1 || '',
          line2: payPalSourceAddress.line2 || '',
          city: payPalSourceAddress.city || '',
          state: payPalSourceAddress.state || '',
          zip: payPalSourceAddress.zip || '',
          country: payPalSourceAddress.country || 'USA',
        };
      } else {
        // If somehow no shipping info is available for PayPal, use the derived one (which might be null)
        apiBillingAddress = derivedBillingAddress;
      }
      // Log inside the PayPal check
      if (isNonProduction) {
        console.log(
          '[onSubmit] INSIDE PayPal check - payPalSourceAddress:',
          payPalSourceAddress
        );
        console.log(
          '[onSubmit] INSIDE PayPal check - constructed apiBillingAddress:',
          apiBillingAddress
        );
      }
      // finalizeOrder requires a shipping address, even for PayPal.
      // Use the actual shipping address if needed, otherwise use the billing address as a fallback.
      apiShippingAddress = toBeShipped
        ? sourceShippingAddress
        : apiBillingAddress;
    }

    // --- Step 5: Perform the API call ---
    try {
      // Add specific logging for API payload
      if (isNonProduction) {
        console.log('[onSubmit] Arguments for finalizeOrder API:', {
          invoiceId,
          phone: data.phone || '',
          shipping: apiShippingAddress,
          billing: apiBillingAddress,
        });
      }

      // Ensure apiBillingAddress exists before calling API
      if (!apiBillingAddress) {
        console.error(
          '[onSubmit] Critical Error: Billing address is null before calling finalizeOrder. Aborting.'
        );
        // TODO: Set form error state for user feedback
        setLoading(false);
        return; // Prevent API call
      }

      // Call API with the determined addresses
      finalizeOrder(
        invoiceId,
        data.phone || '',
        apiShippingAddress,
        apiBillingAddress
      )
        .then((response) => {
          const finalizedInvoiceWithResponse = {
            ...invoice,
            invoiceDetails: {
              ...invoice.invoiceDetails,
              order: {
                ...response.order,
                ...(!hasOutstandingBalance && {
                  status: 'Completed' as OrderStatus,
                }),
              },
            },
          };

          if (invoice.tenantId !== undefined) {
            GAEvent('Proceed to Pay', invoice.tenantId);
          }

          // if (!isUnmounting.current) {
          // By the time this continuation executes, the component might be
          // torn down. This isn't a big deal in the wild, but this raises a
          // fuss during test runs because it could indicate a memory leak.
          if (response?.paymentFormInfo) {
            setPaymentFormInfo({
              paymentProcessor:
                response.paymentFormInfo.paymentProcessor || undefined,
              paymentFormUrl: response.paymentFormInfo.paymentFormUrl || '',
            });
          }
          setInvoiceDetails(finalizedInvoiceWithResponse);
          // Return something to ensure proper promise chaining
          return response;
        })
        .then((response) => {
          if (hasOutstandingBalance) {
            navigate(`/invoice/${invoiceId}/pay`);
          } else {
            // TODO: Add better logic here for edge cases.
            navigate(`/invoice/${invoiceId}/confirmation`);
          }
          return response;
        })
        .catch((error) => {
          Sentry.captureMessage(error, 'warning');
          setFinalizeOrderError(true);
          setLoading(false);
          throw error;
        });
    } catch (error) {
      Sentry.captureException(error);
      setFinalizeOrderError(true);
      setLoading(false);
    }
  };

  const handleBillingSameAsShippingChange = (
    event: ChangeEvent<HTMLInputElement>
  ): void => {
    const isSame = event.target.value === 'same';
    setBillingSameAsShipping(isSame);

    if (!isSame) {
      setValue(fieldPath.billingFirstName, '');
      setValue(fieldPath.billingLastName, '');
      setValue(fieldPath.billingLine1, '');
      setValue(fieldPath.billingLine2, '');
      setValue(fieldPath.billingCity, '');
      setValue(fieldPath.billingState, '');
      setValue(fieldPath.billingZip, '');
      setValue(fieldPath.billingCountry, 'USA');
    }
  };
  const sameAsAddress =
    watch().shippingAddress || customerInfo?.shippingAddress || {};
  const isAllEmptyStringsAddress = !Object.values(sameAsAddress).some(
    (x) => x !== null && x !== ''
  );
  const handlePaymentTypeChange = (
    event: ChangeEvent<HTMLInputElement>
  ): void => {
    setPaymentType((event.target as HTMLInputElement).value);
  };

  // Scroll to the first error field when the form is submitted
  useEffect(() => {
    const hasErrors = Object.keys(errors).length > 0;
    
    if (hasErrors) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const findFirstErrorWithRef = (obj: any): { ref?: HTMLElement } | undefined => {
        if (obj?.ref) return obj;
        return Object.values(obj)
          .filter(val => typeof val === 'object' && val !== null)
          .map(findFirstErrorWithRef)
          .find(Boolean);
      };

      const firstErrorField = findFirstErrorWithRef(errors);
      if (firstErrorField?.ref) {
        firstErrorField.ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstErrorField.ref.focus({ preventScroll: true });
      }
    }
  }, [errors]);

  if (!invoice?.invoiceDetails?.order) {
    return <div>Loading...</div>;
  }

  return (
    <div className="col-12 my-4">
      {errorsArray?.length && (
        <Alert severity="error" sx={{ marginBottom: '1rem' }}>
          There was a problem verifying your billing address. Please double
          check your entries.
        </Alert>
      )}
      <form ref={formRef} onSubmit={handleSubmit(onSubmit)} noValidate>
        <h3 className="mb-4 mt-2">Contact Information</h3>
        <TextField
          id="formInvoicePortalBilling_ContactInfo_Phone"
          className="mb-3"
          label="Phone (Optional)"
          variant="outlined"
          type="tel"
          fullWidth
          inputProps={{
            'data-testid': 'contact-phone',
            maxLength: 255,
          }}
          defaultValue={customerInfo?.phone}
          // TODO: We won't need to do this once we update @mui/material to 5.13 and up
          InputLabelProps={{
            shrink: !!watch(fieldPath.phone),
          }}
          {...register(fieldPath.phone, {
            pattern: {
              message: 'Phone is invalid',
              value: phoneNumberRegex,
            },
            onBlur: (e) => setValue(fieldPath.phone, e.target.value.trim()),
          })}
          helperText={errors.phone?.message}
          error={!!errors.phone}
        />
        {toBeShipped && (
          <>
            <h3 className="mb-4 mt-2" id="Shipping-Information">
              Shipping Information
            </h3>
            <TextField
              id="formInvoicePortalBilling_ShippingInformation_firstName"
              className="mb-3"
              label="First Name"
              variant="outlined"
              type="text"
              fullWidth
              inputProps={{
                maxLength: 255,
              }}
              defaultValue={customerInfo?.shippingAddress?.firstName}
              InputLabelProps={{
                shrink: !!watch(fieldPath.shippingFirstName),
              }}
              {...register(fieldPath.shippingFirstName, {
                required: 'First Name is required',
                validate: {
                  noEmoji: (value) =>
                    !emojiCheck(value || '') || 'Emojis are invalid',
                  noWhiteSpacesOnly: (value) =>
                    !!(value || '').trim() || 'First Name is required',
                },
                onBlur: (e) => {
                  setValue(fieldPath.shippingFirstName, e.target.value.trim());
                  if (!e.target.value.trim().length) {
                    setError(fieldPath.shippingFirstName, {
                      message: 'First Name is required',
                    });
                  }
                },
              })}
              helperText={errors.shippingAddress?.firstName?.message}
              error={!!errors.shippingAddress?.firstName}
            />
            <TextField
              id="formInvoicePortalBilling_ShippingInformation_lastName"
              className="mb-3"
              label="Last Name"
              variant="outlined"
              type="text"
              fullWidth
              inputProps={{
                maxLength: 255,
              }}
              defaultValue={customerInfo?.shippingAddress?.lastName}
              InputLabelProps={{
                shrink: !!watch(fieldPath.shippingLastName),
              }}
              {...register(fieldPath.shippingLastName, {
                required: 'Last Name is required',
                validate: {
                  noEmoji: (value) =>
                    !emojiCheck(value || '') || 'Emojis are invalid',
                  noWhiteSpacesOnly: (value) =>
                    !!(value || '').trim() || 'Last Name is required',
                },
                onBlur: (e) => {
                  setValue(fieldPath.shippingLastName, e.target.value.trim());
                  if (!e.target.value.trim().length) {
                    setError(fieldPath.shippingLastName, {
                      message: 'Last Name is required',
                    });
                  }
                },
              })}
              helperText={errors.shippingAddress?.lastName?.message}
              error={!!errors.shippingAddress?.lastName}
            />
            <TextField
              id="formInvoicePortalBilling_ShippingInformation_AddressLine1"
              className="mb-3"
              label="Address"
              variant="outlined"
              type="text"
              fullWidth
              inputProps={{
                'data-testid': 'shipping-line1',
                maxLength: 255,
              }}
              defaultValue={customerInfo?.shippingAddress?.line1}
              InputLabelProps={{
                shrink: !!watch(fieldPath.shippingLine1),
              }}
              {...register(fieldPath.shippingLine1, {
                required: 'Address is required',
                validate: {
                  noEmoji: (value) =>
                    !emojiCheck(value || '') || 'Emojis are invalid',
                  noWhiteSpacesOnly: (value) =>
                    !!(value || '').trim() || 'Address is required',
                },
                onBlur: (e) => {
                  setValue(fieldPath.shippingLine1, e.target.value.trim());
                  if (!e.target.value.trim().length) {
                    setError(fieldPath.shippingLine1, {
                      message: 'Address is required',
                    });
                  }
                },
              })}
              helperText={errors.shippingAddress?.line1?.message}
              error={!!errors.shippingAddress?.line1}
            />
            <TextField
              id="formInvoicePortalBilling_ShippingInformation_AddressLine2"
              className="mb-3"
              label="Apartment, Suite, etc (Optional)"
              variant="outlined"
              type="text"
              fullWidth
              inputProps={{
                'data-testid': 'shipping-line2',
                maxLength: 255,
              }}
              defaultValue={customerInfo?.shippingAddress?.line2}
              InputLabelProps={{
                shrink: !!watch(fieldPath.shippingLine2),
              }}
              {...register(fieldPath.shippingLine2, {
                validate: {
                  noEmoji: (value) =>
                    !emojiCheck(value || '') || 'Emojis are invalid',
                },
                onBlur: (e) =>
                  setValue(fieldPath.shippingLine2, e.target.value.trim()),
              })}
              helperText={errors.shippingAddress?.line2?.message}
              error={!!errors.shippingAddress?.line2}
            />
            <TextField
              id="formInvoicePortalBilling_ShippingInformation_City"
              className="mb-3"
              label="City"
              variant="outlined"
              type="text"
              fullWidth
              inputProps={{
                'data-testid': 'shipping-city',
                maxLength: 50,
              }}
              defaultValue={customerInfo?.shippingAddress?.city}
              InputLabelProps={{
                shrink: !!watch(fieldPath.shippingCity),
              }}
              {...register(fieldPath.shippingCity, {
                required: 'City is required',
                validate: {
                  noEmoji: (value) =>
                    !emojiCheck(value || '') || 'Emojis are invalid',
                  noWhiteSpacesOnly: (value) =>
                    !!(value || '').trim() || 'City is required',
                },
                onBlur: (e) => {
                  setValue(fieldPath.shippingCity, e.target.value.trim());
                  if (!e.target.value.trim().length) {
                    setError(fieldPath.shippingCity, {
                      message: 'City is required',
                    });
                  }
                },
              })}
              helperText={errors.shippingAddress?.city?.message}
              error={!!errors.shippingAddress?.city}
            />
            <TextField
              id="formInvoicePortalBilling_ShippingInformation_State"
              className="mb-3"
              label="State"
              variant="outlined"
              select
              fullWidth
              SelectProps={{ native: true }}
              inputProps={{
                'data-testid': 'shipping-state',
              }}
              {...register(fieldPath.shippingState, {
                required: 'State is required',
              })}
              value={watch(fieldPath.shippingState) || ''}
              helperText={errors.shippingAddress?.state?.message}
              error={!!errors.shippingAddress?.state}
            >
              <option value=""> </option>
              {SupportedStatesTerritoriesArmedForces.map((state) => (
                <option key={state.isoCode} value={state.isoCode}>
                  {state.value}
                </option>
              ))}
            </TextField>
            <TextField
              id="formInvoicePortalBilling_ShippingInformation_Zip"
              className="mb-3"
              label="ZIP Code"
              variant="outlined"
              type="text"
              fullWidth
              inputProps={{
                'data-testid': 'shipping-zip-code',
                maxLength: 255,
              }}
              defaultValue={customerInfo?.shippingAddress?.zip}
              InputLabelProps={{
                shrink: !!watch(fieldPath.shippingZip),
              }}
              {...register(fieldPath.shippingZip, {
                required: 'ZIP Code is required',
                pattern: {
                  message: 'ZIP Code is invalid',
                  value: usaZipRegex,
                },
                onBlur: (e) => {
                  setValue(fieldPath.shippingZip, e.target.value.trim());
                  if (!e.target.value.trim().length) {
                    setError(fieldPath.shippingZip, {
                      message: 'ZIP Code is required',
                    });
                  }
                },
              })}
              helperText={errors.shippingAddress?.zip?.message}
              error={!!errors.shippingAddress?.zip}
            />
          </>
        )}
        {isNonProduction && (
          <Chip
            label={`DEV ONLY: Store Only Allows These Payment Processors: ${invoice?.paymentProcessors?.toString()}`}
            sx={{ fontWeight: 'bold' }}
          />
        )}
        {invoice?.paymentProcessors?.includes('PayPal') &&
          hasOutstandingBalance && (
            <>
              <Typography component="h3" variant="h4" className="my-3">
                Pay with
              </Typography>
              <FormControl>
                <RadioGroup
                  aria-labelledby="demo-radio-buttons-group-label"
                  defaultValue="female"
                  name="radio-buttons-group"
                  value={paymentType}
                  className={Styles.paymentRadio}
                  onChange={handlePaymentTypeChange}
                >
                  <FormControlLabel
                    value="Card"
                    control={<Radio />}
                    aria-label="Credit Card, Visa, Mastercard, Discover, American Express"
                    label={
                      <div className={Styles.card_logos_container}>
                        <CreditCardLogos />
                      </div>
                    }
                  />
                  <FormControlLabel
                    value="PayPal"
                    control={<Radio />}
                    aria-label="PayPal"
                    label={
                      <div>
                        <img
                          src={PayPalLogo}
                          className={Styles.paypalLogo}
                          alt="PayPal"
                        />
                      </div>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </>
          )}
        {paymentType === 'Card' && hasOutstandingBalance && (
          <>
            <Typography component="h3" variant="h4" className="my-3">
              Billing Address
            </Typography>
            {toBeShipped && (
              <FormControl>
                <RadioGroup
                  aria-labelledby="billing-address-group-label"
                  name="billing-address-group"
                  value={!billingSameAsShipping ? 'different' : 'same'}
                  onChange={handleBillingSameAsShippingChange}
                  data-testid="billing-address-radio-group"
                >
                  <FormControlLabel
                    value="same"
                    control={<Radio />}
                    label="Billing address is same as shipping"
                    data-testid="same-billing-address-label"
                  />
                  {billingSameAsShipping &&
                    !isAllEmptyStringsAddress &&
                    customerInfo && (
                      <section
                        data-testid="billing-address-customer-info"
                        className="ps-5 pb-3"
                      >
                        <Typography>
                          <b>{`${watchedShippingFirstName || ''} ${
                            watchedShippingLastName || ''
                          }`}</b>
                        </Typography>

                        <Typography>
                          <b>
                            {watchedShippingLine1 || ''}
                            {watchedShippingLine2 && ` ${watchedShippingLine2}`}
                          </b>
                        </Typography>
                        {watchedShippingCity && watchedShippingState && (
                          <Typography>
                            <b>
                              {watchedShippingCity || ''}
                              ,&nbsp;
                              {watchedShippingState || ''}
                              &nbsp;
                              {watchedShippingZip || ''}
                            </b>
                          </Typography>
                        )}
                      </section>
                    )}
                  <FormControlLabel
                    value="different"
                    control={<Radio />}
                    label="Enter a different billing address"
                    data-testid="different-billing-address-label"
                  />
                </RadioGroup>
              </FormControl>
            )}
            {!billingSameAsShipping && (
              <>
                <TextField
                  id="formInvoicePortalBilling_BillingInformation_firstName"
                  className="mb-3"
                  label="First Name"
                  variant="outlined"
                  type="text"
                  fullWidth
                  inputProps={{
                    maxLength: 255,
                  }}
                  InputLabelProps={{
                    shrink: !!watch(fieldPath.billingFirstName),
                  }}
                  {...register(fieldPath.billingFirstName, {
                    required: billingSameAsShipping
                      ? false
                      : 'First Name is required',
                    validate: !billingSameAsShipping
                      ? {
                          noEmoji: (value) =>
                            !emojiCheck(value || '') || 'Emojis are invalid',
                          noWhiteSpacesOnly: (value) =>
                            !billingSameAsShipping
                              ? !!(value || '').trim() ||
                                'First Name is required'
                              : true,
                        }
                      : undefined,
                    onBlur: (e) => {
                      setValue(
                        fieldPath.billingFirstName,
                        e.target.value.trim()
                      );
                      if (
                        !billingSameAsShipping &&
                        !e.target.value.trim().length
                      ) {
                        setError(fieldPath.billingFirstName, {
                          message: 'First Name is required',
                        });
                      }
                    },
                  })}
                  helperText={errors.billingAddress?.firstName?.message}
                  error={!!errors.billingAddress?.firstName}
                />
                <TextField
                  id="formInvoicePortalBilling_BillingInformation_lastName"
                  className="mb-3"
                  label="Last Name"
                  variant="outlined"
                  type="text"
                  fullWidth
                  inputProps={{
                    maxLength: 255,
                  }}
                  InputLabelProps={{
                    shrink: !!watch(fieldPath.billingLastName),
                  }}
                  {...register(fieldPath.billingLastName, {
                    required: billingSameAsShipping
                      ? false
                      : 'Last Name is required',
                    validate: !billingSameAsShipping
                      ? {
                          noEmoji: (value) =>
                            !emojiCheck(value || '') || 'Emojis are invalid',
                          noWhiteSpacesOnly: (value) =>
                            !billingSameAsShipping
                              ? !!(value || '').trim() ||
                                'Last Name is required'
                              : true,
                        }
                      : undefined,
                    onBlur: (e) => {
                      setValue(
                        fieldPath.billingLastName,
                        e.target.value.trim()
                      );
                      if (
                        !billingSameAsShipping &&
                        !e.target.value.trim().length
                      ) {
                        setError(fieldPath.billingLastName, {
                          message: 'Last Name is required',
                        });
                      }
                    },
                  })}
                  helperText={errors.billingAddress?.lastName?.message}
                  error={!!errors.billingAddress?.lastName}
                />
                <TextField
                  id="formInvoicePortalBilling_BillingInformation_AddressLine1"
                  className="mb-3"
                  label="Address"
                  variant="outlined"
                  type="text"
                  fullWidth
                  inputProps={{
                    'data-testid': 'billing-line1',
                    maxLength: 255,
                  }}
                  InputLabelProps={{
                    shrink: !!watch(fieldPath.billingLine1),
                  }}
                  {...register(fieldPath.billingLine1, {
                    required: 'Address is required',
                    validate: {
                      noEmoji: (value) =>
                        !emojiCheck(value || '') || 'Emojis are invalid',
                      noWhiteSpacesOnly: (value) =>
                        !!(value || '').trim() || 'Address is required',
                    },
                    onBlur: (e) => {
                      setValue(fieldPath.billingLine1, e.target.value.trim());
                      if (!e.target.value.trim().length) {
                        setError(fieldPath.billingLine1, {
                          message: 'Address is required',
                        });
                      }
                    },
                  })}
                  helperText={errors.billingAddress?.line1?.message}
                  error={!!errors.billingAddress?.line1}
                  disabled={billingSameAsShipping}
                />
                <TextField
                  id="formInvoicePortalBilling_BillingInformation_AddressLine2"
                  className="mb-3"
                  label="Apartment, Suite, etc (Optional)"
                  variant="outlined"
                  type="text"
                  fullWidth
                  inputProps={{
                    'data-testid': 'billing-line2',
                    maxLength: 255,
                  }}
                  InputLabelProps={{
                    shrink: !!watch(fieldPath.billingLine2),
                  }}
                  {...register(fieldPath.billingLine2, {
                    validate: {
                      noEmoji: (value) =>
                        !emojiCheck(value || '') || 'Emojis are invalid',
                    },
                    onBlur: (e) =>
                      setValue(fieldPath.billingLine2, e.target.value.trim()),
                  })}
                  helperText={errors.billingAddress?.line2?.message}
                  error={!!errors.billingAddress?.line2}
                  disabled={billingSameAsShipping}
                />
                <TextField
                  id="formInvoicePortalBilling_BillingInformation_City"
                  className="mb-3"
                  label="City"
                  variant="outlined"
                  type="text"
                  fullWidth
                  inputProps={{
                    'data-testid': 'billing-city',
                    maxLength: 50,
                  }}
                  InputLabelProps={{
                    shrink: !!watch(fieldPath.billingCity),
                  }}
                  {...register(fieldPath.billingCity, {
                    required: 'City is required',
                    validate: {
                      noEmoji: (value) =>
                        !emojiCheck(value || '') || 'Emojis are invalid',
                      noWhiteSpacesOnly: (value) =>
                        !!(value || '').trim() || 'City is required',
                    },
                    onBlur: (e) => {
                      setValue(fieldPath.billingCity, e.target.value.trim());
                      if (!e.target.value.trim().length) {
                        setError(fieldPath.billingCity, {
                          message: 'City is required',
                        });
                      }
                    },
                  })}
                  helperText={errors.billingAddress?.city?.message}
                  error={!!errors.billingAddress?.city}
                  disabled={billingSameAsShipping}
                />
                <TextField
                  id="formInvoicePortalBilling_BillingInformation_State"
                  className="mb-3"
                  label="State"
                  variant="outlined"
                  select
                  fullWidth
                  SelectProps={{ native: true }}
                  inputProps={{
                    'data-testid': 'billing-state',
                  }}
                  {...register(fieldPath.billingState, {
                    required: 'State is required',
                  })}
                  value={watch(fieldPath.billingState) || ''}
                  helperText={errors.billingAddress?.state?.message}
                  error={!!errors.billingAddress?.state}
                  disabled={billingSameAsShipping}
                >
                  <option value=""> </option>
                  {SupportedStatesTerritoriesArmedForces.map((state) => (
                    <option key={state.isoCode} value={state.isoCode}>
                      {state.value}
                    </option>
                  ))}
                </TextField>
                <TextField
                  id="formInvoicePortalBilling_BillingInformation_Zip"
                  className="mb-3"
                  label="ZIP Code"
                  variant="outlined"
                  type="text"
                  fullWidth
                  inputProps={{
                    'data-testid': 'billing-zip-code',
                    maxLength: 255,
                  }}
                  InputLabelProps={{
                    shrink: !!watch(fieldPath.billingZip),
                  }}
                  {...register(fieldPath.billingZip, {
                    required: 'ZIP Code is required',
                    pattern: {
                      message: 'ZIP Code is invalid',
                      value: usaZipRegex,
                    },
                    onBlur: (e) => {
                      setValue(fieldPath.billingZip, e.target.value.trim());
                      if (!e.target.value.trim().length) {
                        setError(fieldPath.billingZip, {
                          message: 'ZIP Code is required',
                        });
                      }
                    },
                  })}
                  helperText={errors.billingAddress?.zip?.message}
                  error={!!errors.billingAddress?.zip}
                  disabled={billingSameAsShipping}
                />
              </>
            )}
          </>
        )}
        {finalizeOrderError && (
          <Alert severity="error" sx={{ margin: '1rem 0' }}>
            Something went wrong. Please try refreshing your page.
          </Alert>
        )}
        <Grid item xs={12} className="mt-3">
          <Typography variant="body1">
            <small>
              By clicking the {orderPaymentCheck} button, you confirm that you
              have read and understood and accept our{' '}
              <Link
                to={termsOfUseUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Use
              </Link>{' '}
              and{' '}
              <Link
                to={privacyPolicyUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </Link>
              .
            </small>
          </Typography>
        </Grid>
        <Grid item xs={12} textAlign={'center'} sx={{ padding: 2 }}>
          <Button
            color="primary"
            variant="contained"
            fullWidth
            type="submit"
            disabled={loading || finalizeOrderError}
            data-testid="submit-button"
          >
            {loading ? (
              <CircularProgress
                size="1.5rem"
                color="inherit"
                data-testid="loading-spinner"
              />
            ) : (
              orderPaymentCheck
            )}
          </Button>
        </Grid>
      </form>
    </div>
  );
}

export default InvoicePortalBilling;
